import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Network, ArrowRight, TrendingUp, Users, Wallet, Shield, Palmtree, Waves, Hotel, Coins, Sparkles, Handshake, Leaf, Sun, Mountain, Mail, Phone, MapPin } from "lucide-react";

const Feature = ({ icon: Icon, title, body }) => (
  <div data-testid={`feature-${title.toLowerCase().replace(/\s/g,'-')}`} className="p-6 border border-border/60 rounded-2xl bg-card/40 hover:border-primary/50 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <div className="font-display font-bold text-lg mb-2">{title}</div>
    <div className="text-sm text-muted-foreground leading-relaxed">{body}</div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen relative">
      <div
        className="absolute inset-0 opacity-40 -z-0"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1770486036751-e55247238964?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 40%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 40%, transparent 100%)",
        }}
      />
      <div className="relative z-10">
        <header className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/asiyanna-logo.jpg" alt="asiyanna" className="w-11 h-11 rounded-lg object-cover glow-pill" />
            <span className="font-display font-bold tracking-tight text-xl lowercase">asiyanna</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button data-testid="landing-login-btn" variant="ghost" className="rounded-full">Log in</Button></Link>
          </div>
        </header>

        <section className="max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="tag mb-6"><span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Hospitality · Tea Gardens · Finance</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] max-w-4xl">
            Where <span className="text-primary">resort escapes</span> meet <span className="text-accent">real wealth</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            asiyanna small capital pvt ltd blends three worlds that rarely meet — a curated collection of coastal hotel resorts,
            heritage tea estates in the misty highlands, and a multi-level financial network that pays every member for the
            growth they help create. Stay with us, sip with us, earn with us.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login">
              <Button data-testid="hero-cta-btn" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 glow-pill">
                Log in to your account <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-16">
          <div data-testid="pillar-tea" className="rounded-3xl border border-primary/30 bg-card/40 overflow-hidden grid grid-cols-1 lg:grid-cols-2">
            <div
              className="h-72 lg:h-auto min-h-[340px] relative"
              style={{
                backgroundImage: "url(/tea-garden.jpg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/70 lg:to-card/40" />
              <div className="absolute top-4 left-4 tag"><Leaf className="w-3.5 h-3.5 text-primary" /> Tea Gardens</div>
            </div>
            <div className="p-8 lg:p-10">
              <h2 className="font-display font-bold text-3xl mb-4">Rolling green. Slow-brewed patience.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Our tea estates unfold across the misty ridges of the Western Ghats — kilometres of emerald bushes clipped by hand,
                touched by monsoon fog, and dried on wooden trays the way it has been done for a hundred years. From single-estate
                orthodox blacks to delicate first-flush greens, every leaf carries the altitude of the hills and the honesty of the
                farmers who tend them. Visit the gardens, walk the pluck lines at dawn, taste at the factory floor — or simply
                order a tin and let the highlands come to you.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Mountain className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Altitude</div>
                  <div className="font-display font-bold">1,400m+</div>
                </div>
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Leaf className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Estates</div>
                  <div className="font-display font-bold">6</div>
                </div>
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Sun className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Harvests</div>
                  <div className="font-display font-bold">3 flushes</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-testid="pillar-hotel" className="rounded-3xl border border-primary/30 overflow-hidden bg-card/40 flex flex-col">
            <div
              className="relative h-56"
              style={{
                backgroundImage: "url(/hotel-resort.jpg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
              <div className="absolute top-4 left-4 tag"><Palmtree className="w-3.5 h-3.5 text-primary" /> Hotel & Resorts</div>
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Flagship property</div>
                <div className="font-display font-bold text-2xl">asiyanna Varkala Cliffs Resort</div>
              </div>
            </div>
            <div className="p-8 relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/10 blur-2xl" />
              <h2 className="font-display font-bold text-3xl mb-4">Coastlines curated. Comfort delivered.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                Our resort portfolio hugs the palm-fringed coasts and misty hills of southern India — hand-picked properties where
                spice-scented breezes drift through open corridors, sunsets are on the schedule, and every room comes with a story.
                Whether it's a weekend getaway, a family celebration, or a corporate retreat, asiyanna promises quiet luxury,
                honest local cuisine, and staff who remember your name before you leave the lobby.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Waves className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Beachfront</div>
                  <div className="font-display font-bold">12 stays</div>
                </div>
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Hotel className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Suites</div>
                  <div className="font-display font-bold">240+</div>
                </div>
                <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                  <Sparkles className="w-4 h-4 text-primary mb-2" />
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rating</div>
                  <div className="font-display font-bold">4.8 / 5</div>
                </div>
              </div>
            </div>
          </div>

          <div data-testid="pillar-finance" className="p-8 rounded-3xl border border-accent/30 bg-gradient-to-br from-card/70 to-card/20 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-accent/10 blur-2xl" />
            <div className="tag mb-4"><Coins className="w-3.5 h-3.5 text-accent" /> Financial Network</div>
            <h2 className="font-display font-bold text-3xl mb-4">Small capital. Compounding trust.</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              asiyanna's financial arm is built on a simple idea — the people who bring the community together deserve to share in
              the growth. Members activate with a one-time joining fee, onboard verified customers, and unlock structured commissions
              across five downline levels. Every rupee is tracked in a transparent ledger, KYC-gated for payouts, and released
              through Stripe or Razorpay. It's not passive income theatre; it's a real network economy with real audit trails.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                <TrendingUp className="w-4 h-4 text-accent mb-2" />
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Levels</div>
                <div className="font-display font-bold">5 deep</div>
              </div>
              <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                <Handshake className="w-4 h-4 text-accent mb-2" />
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Partner</div>
                <div className="font-display font-bold">of the company</div>
              </div>
              <div className="p-3 rounded-xl bg-background/40 border border-border/40">
                <Shield className="w-4 h-4 text-accent mb-2" />
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payouts</div>
                <div className="font-display font-bold">KYC-safe</div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-24">
          <h3 className="font-display font-bold text-2xl mb-6">Why asiyanna</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Feature icon={Users} title="Referral Codes" body="Every member gets a unique code and downline dashboard. Share it once, watch your tree grow in real time." />
            <Feature icon={TrendingUp} title="5-Level Commission" body="Direct sign-ups and four downline levels earn — plus you become a true partner of the company as your network grows." />
            <Feature icon={Wallet} title="Instant Payouts" body="Stripe and Razorpay top-ups credit your wallet within seconds. Withdrawals reviewed and released by admin." />
            <Feature icon={Shield} title="Secure by Design" body="JWT httpOnly cookies, bcrypt hashing, KYC-gated payouts, and a full audit ledger visible to admins." />
          </div>
        </section>

        <footer className="border-t border-white/5 py-10">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src="/asiyanna-logo.jpg" alt="asiyanna" className="w-9 h-9 rounded-lg object-cover" />
                <span className="font-display font-bold lowercase text-lg">asiyanna</span>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                asiyanna small capital pvt ltd — hospitality, tea gardens & financial network.
              </div>
              <div className="text-xs text-muted-foreground mt-3 font-mono">
                CIN: U64990WR2026PTC295530
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Contact us</div>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-primary" /> <a data-testid="footer-email" href="mailto:hello@asiyanna.in" className="hover:text-primary">hello@asiyanna.in</a></li>
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-primary" /> <a data-testid="footer-phone" href="tel:+911800000000" className="hover:text-primary">+91 18000 00000</a></li>
                <li className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-primary mt-1" /> <span className="text-muted-foreground">Registered office — West Bengal, India</span></li>
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Business</div>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>Hotels & Resorts</li>
                <li>Tea Gardens</li>
                <li>Financial Network</li>
              </ul>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.25em] text-muted-foreground">
            <div>© asiyanna small capital pvt ltd 2021-2026.</div>
            <div className="font-mono normal-case tracking-normal text-[10px]">CIN U64990WR2026PTC295530</div>
          </div>
        </footer>
      </div>
    </div>
  );
}
